/**
 * Combat System - handles unit combat behavior
 * Optimized to use squared distances and reduce allocations
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { CombatComponent } from '@/game/components/Combat';
import type { HealthComponent } from '@/game/components/Health';
import type { FactionComponent } from '@/game/components/Faction';
import type { MovementComponent } from '@/game/components/Movement';
import { setMovementTarget, saveMovement, restoreMovement } from '@/game/systems/MovementSystem';
import type { UnitComponent } from '@/game/components/Unit';
import type { BuildingComponent } from '@/game/components/Building';
import { SpatialHash, type Positioned } from '@/core/utils/SpatialHash';
import { EventBus } from '@/core/events/EventBus';
import { getCounterBonus, FACTIONS } from '@/game/factions/FactionData';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { Entity } from '@/types';
import { getTerrainCombatModifiers, getFullTerrainModifier } from '@/rendering/terrain/TerrainGenerator';
import type { UnitType } from '@/types';

interface CombatUnit extends Positioned {
  entity: Entity;
  factionId: string;
  isBuilding: boolean;
}

export class CombatSystem implements System {
  private spatialHash: SpatialHash<CombatUnit>;
  private factionManager: FactionManager;
  private gameTime: number = 0;

  // Reusable arrays to reduce allocations
  private entitiesToRemove: Entity[] = [];

  constructor(factionManager: FactionManager) {
    this.spatialHash = new SpatialHash(5);
    this.factionManager = factionManager;
  }

  update(world: World, deltaTime: number): void {
    this.gameTime += deltaTime;

    // Rebuild spatial hash
    this.spatialHash.clear();
    const combatEntities = world.query([
      ComponentTypes.Position,
      ComponentTypes.Combat,
      ComponentTypes.Faction,
      ComponentTypes.Health,
    ]);

    // First pass: build spatial hash and mark dead entities
    this.entitiesToRemove.length = 0;

    for (let i = 0, len = combatEntities.length; i < len; i++) {
      const entity = combatEntities[i];
      const health = world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;

      // Track dead entities for removal
      if (health.current <= 0) {
        this.entitiesToRemove.push(entity);
        continue;
      }

      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);

      this.spatialHash.insert({
        entity,
        x: position.x,
        z: position.z,
        factionId: faction.factionId,
        isBuilding: building !== null,
      });
    }

    // Process combat for each living entity
    for (let i = 0, len = combatEntities.length; i < len; i++) {
      const entity = combatEntities[i];
      const health = world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;

      // Skip dead entities
      if (health.current <= 0) continue;

      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);
      // Only towers can attack - skip other buildings
      if (building && building.buildingType !== 'tower') continue;

      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const combat = world.getComponent<CombatComponent>(entity, ComponentTypes.Combat)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const movement = world.getComponent<MovementComponent>(entity, ComponentTypes.Movement);

      // Pre-compute squared ranges for this unit
      const rangeSq = combat.range * combat.range;
      const disengageRangeSq = (combat.range * 2) * (combat.range * 2);
      // Buildings (towers) can't move, so only search within attack range
      const searchRange = movement ? combat.range * 1.5 : combat.range;

      // Track if we had a target before this frame
      const hadTarget = combat.target !== null;

      // Find or validate target
      if (combat.target !== null) {
        const targetHealth = world.getComponent<HealthComponent>(combat.target, ComponentTypes.Health);
        const targetPos = world.getComponent<PositionComponent>(combat.target, ComponentTypes.Position);

        // Clear target if dead or doesn't exist
        if (!targetHealth || targetHealth.current <= 0 || !targetPos) {
          combat.target = null;
        } else {
          // Check if target is still in range (with buffer) - use squared distance
          const dx = targetPos.x - position.x;
          const dz = targetPos.z - position.z;
          const distSq = dx * dx + dz * dz;

          // 反击目标有更大的追击范围（15单位），确保近战兵能追上远程兵/箭塔
          const isRetaliationTarget = combat.target === combat.lastAttacker;
          const RETALIATION_CHASE_RANGE_SQ = 15 * 15; // 225
          const effectiveDisengageRangeSq = isRetaliationTarget ? RETALIATION_CHASE_RANGE_SQ : disengageRangeSq;

          if (distSq > effectiveDisengageRangeSq) {
            combat.target = null;
          }
        }
      }

      // If we just lost our target, restore saved movement destination
      if (hadTarget && combat.target === null && movement) {
        restoreMovement(movement);
      }

      // Find new target if needed
      if (combat.target === null) {
        let newTarget: Entity | null = null;

        // 反击机制：优先攻击最近攻击过自己的敌人（5秒内有效）
        const RETALIATION_TIMEOUT = 5.0;
        if (combat.lastAttacker !== null &&
            (this.gameTime - combat.lastAttackerTime) < RETALIATION_TIMEOUT) {
          const attackerHealth = world.getComponent<HealthComponent>(combat.lastAttacker, ComponentTypes.Health);
          const attackerFaction = world.getComponent<FactionComponent>(combat.lastAttacker, ComponentTypes.Faction);

          // 确保攻击者还活着且是敌人
          if (attackerHealth && attackerHealth.current > 0 &&
              attackerFaction && this.factionManager.isEnemy(faction.factionId, attackerFaction.factionId as never)) {
            newTarget = combat.lastAttacker;
          } else {
            // 攻击者已死亡或不再是敌人，清除记录
            combat.lastAttacker = null;
          }
        }

        // 如果没有反击目标，按原逻辑寻找目标
        if (newTarget === null) {
          let bestUnitTarget: Entity | null = null;
          let bestUnitDistSq = Infinity;
          let bestBuildingTarget: Entity | null = null;
          let bestBuildingDistSq = Infinity;

          const nearbyTargets = this.spatialHash.queryRadius(position.x, position.z, searchRange);

          for (let j = 0, jLen = nearbyTargets.length; j < jLen; j++) {
            const target = nearbyTargets[j];
            if (target.entity === entity) continue;
            if (!this.factionManager.isEnemy(faction.factionId, target.factionId as never)) continue;

            const targetHealth = world.getComponent<HealthComponent>(target.entity, ComponentTypes.Health);
            if (!targetHealth || targetHealth.current <= 0) continue;

            // Use squared distance for comparison
            const dx = target.x - position.x;
            const dz = target.z - position.z;
            const distSq = dx * dx + dz * dz;

            if (target.isBuilding) {
              if (distSq < bestBuildingDistSq) {
                bestBuildingDistSq = distSq;
                bestBuildingTarget = target.entity;
              }
            } else {
              if (distSq < bestUnitDistSq) {
                bestUnitDistSq = distSq;
                bestUnitTarget = target.entity;
              }
            }
          }

          newTarget = bestUnitTarget ?? bestBuildingTarget;
        }

        // If acquiring a new target, save current movement destination first
        if (newTarget !== null && movement) {
          if ((movement.finalTargetX !== null || movement.finalTargetZ !== null) &&
              movement.savedTargetX === null && movement.savedTargetZ === null) {
            saveMovement(movement);
          }
        }

        combat.target = newTarget;
      }

      // Move toward or attack target
      if (combat.target !== null) {
        const targetPos = world.getComponent<PositionComponent>(combat.target, ComponentTypes.Position)!;
        const dx = targetPos.x - position.x;
        const dz = targetPos.z - position.z;
        const distSq = dx * dx + dz * dz;

        if (distSq > rangeSq) {
          // Move toward target - use direct movement during combat (no pathfinding)
          if (movement) {
            movement.targetX = targetPos.x;
            movement.targetZ = targetPos.z;
            movement.finalTargetX = targetPos.x;
            movement.finalTargetZ = targetPos.z;
            movement.path = null; // Direct movement during combat chase
            movement.isPlayerCommand = false;
          } else {
            // Can't move (e.g. tower) - drop target if out of range
            combat.target = null;
          }
        } else {
          // In range - stop moving temporarily to attack
          if (movement) {
            movement.targetX = null;
            movement.targetZ = null;
            movement.finalTargetX = null;
            movement.finalTargetZ = null;
            movement.path = null;
          }

          // Check attack cooldown
          const timeSinceLastAttack = this.gameTime - combat.lastAttackTime;
          if (timeSinceLastAttack >= combat.attackSpeed) {
            combat.lastAttackTime = this.gameTime;
            this.performAttack(world, entity, combat.target, combat, faction);
          }
        }
      }
    }

    // Clean up dead entities (batched at end)
    for (let i = 0, len = this.entitiesToRemove.length; i < len; i++) {
      const entity = this.entitiesToRemove[i];
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);

      if (faction) {
        if (building) {
          EventBus.emit('building:destroyed', {
            entityId: entity,
            factionId: faction.factionId,
          });
        } else {
          EventBus.emit('unit:death', {
            entityId: entity,
            factionId: faction.factionId,
          });
        }
      }
      world.destroyEntity(entity);
    }
  }

  private isRangedAttacker(unitType: string | undefined, buildingType?: string): boolean {
    return unitType === 'archer' || unitType === 'taoist' || buildingType === 'tower';
  }

  private performAttack(
    world: World,
    attacker: Entity,
    target: Entity,
    combat: CombatComponent,
    attackerFaction: FactionComponent
  ): void {
    const attackerUnit = world.getComponent<UnitComponent>(attacker, ComponentTypes.Unit);
    const attackerBuilding = world.getComponent<BuildingComponent>(attacker, ComponentTypes.Building);
    const attackerPos = world.getComponent<PositionComponent>(attacker, ComponentTypes.Position);
    const targetPos = world.getComponent<PositionComponent>(target, ComponentTypes.Position);
    const unitType = attackerUnit?.unitType;
    const buildingType = attackerBuilding?.buildingType;

    // Handle Mage AOE attack
    if (unitType === 'taoist' && attackerPos) {
      this.performAOEAttack(world, attacker, attackerPos, combat, attackerFaction);
      return;
    }

    // Handle visual effects based on unit type or building type
    if (attackerPos && targetPos) {
      const dx = targetPos.x - attackerPos.x;
      const dz = targetPos.z - attackerPos.z;

      // Tower fires arrows like archer
      if (unitType === 'archer' || buildingType === 'tower') {
        // Tower arrows start higher
        const startY = buildingType === 'tower' ? attackerPos.y + 2.5 : attackerPos.y + 0.6;
        EventBus.emit('arrow:fire', {
          startX: attackerPos.x,
          startY: startY,
          startZ: attackerPos.z,
          endX: targetPos.x,
          endY: targetPos.y + 0.4,
          endZ: targetPos.z,
        });
      } else if (unitType === 'sword') {
        EventBus.emit('sword:slash', {
          x: attackerPos.x,
          y: attackerPos.y,
          z: attackerPos.z,
          rotation: Math.atan2(dx, dz),
          targetX: targetPos.x,
          targetY: targetPos.y,
          targetZ: targetPos.z,
          damage: combat.attack,
        });
      } else if (unitType === 'spear') {
        EventBus.emit('spear:thrust', {
          x: attackerPos.x,
          y: attackerPos.y,
          z: attackerPos.z,
          rotation: Math.atan2(dx, dz),
          targetX: targetPos.x,
          targetY: targetPos.y,
          targetZ: targetPos.z,
          damage: combat.attack,
        });
      } else if (unitType === 'cavalry') {
        const distance = Math.sqrt(dx * dx + dz * dz);
        EventBus.emit('cavalry:charge', {
          x: attackerPos.x,
          y: attackerPos.y,
          z: attackerPos.z,
          rotation: Math.atan2(dx, dz),
          targetX: targetPos.x,
          targetY: targetPos.y,
          targetZ: targetPos.z,
          damage: combat.attack,
          speed: Math.min(distance * 0.5, 1.0),
        });
      }
    }

    this.applyDamageToTarget(world, attacker, target, combat, attackerFaction);
  }

  private performAOEAttack(
    world: World,
    attacker: Entity,
    attackerPos: PositionComponent,
    combat: CombatComponent,
    attackerFaction: FactionComponent
  ): void {
    const AOE_RADIUS = 3.0;
    const AOE_DAMAGE_FALLOFF = 0.5;

    const nearbyUnits = this.spatialHash.queryRadius(attackerPos.x, attackerPos.z, AOE_RADIUS);

    // Find primary target and collect enemies in one pass
    let primaryTarget: CombatUnit | null = null;
    let primaryDistSq = Infinity;
    const enemies: CombatUnit[] = [];

    for (let i = 0, len = nearbyUnits.length; i < len; i++) {
      const unit = nearbyUnits[i];
      if (unit.entity === attacker) continue;
      if (!this.factionManager.isEnemy(attackerFaction.factionId, unit.factionId as never)) continue;

      const targetHealth = world.getComponent<HealthComponent>(unit.entity, ComponentTypes.Health);
      if (!targetHealth || targetHealth.current <= 0) continue;

      enemies.push(unit);

      const dx = unit.x - attackerPos.x;
      const dz = unit.z - attackerPos.z;
      const distSq = dx * dx + dz * dz;
      if (distSq < primaryDistSq) {
        primaryDistSq = distSq;
        primaryTarget = unit;
      }
    }

    if (primaryTarget) {
      EventBus.emit('spell:cast', {
        casterId: attacker,
        casterX: attackerPos.x,
        casterY: attackerPos.y,
        casterZ: attackerPos.z,
        targetX: primaryTarget.x,
        targetY: attackerPos.y,
        targetZ: primaryTarget.z,
        spellType: enemies.length > 1 ? 'aoe' : 'lightning',
        targetCount: enemies.length,
      });
    }

    for (let i = 0, len = enemies.length; i < len; i++) {
      const unit = enemies[i];
      this.applyDamageToTarget(
        world, attacker, unit.entity, combat, attackerFaction,
        unit === primaryTarget ? 1.0 : AOE_DAMAGE_FALLOFF
      );
    }
  }

  private applyDamageToTarget(
    world: World,
    attacker: Entity,
    target: Entity,
    combat: CombatComponent,
    attackerFaction: FactionComponent,
    damageMultiplier: number = 1.0
  ): void {
    const targetHealth = world.getComponent<HealthComponent>(target, ComponentTypes.Health);
    const targetFaction = world.getComponent<FactionComponent>(target, ComponentTypes.Faction);

    if (!targetHealth || !targetFaction) return;

    const attackerUnit = world.getComponent<UnitComponent>(attacker, ComponentTypes.Unit);
    const attackerBuilding = world.getComponent<BuildingComponent>(attacker, ComponentTypes.Building);
    const targetUnit = world.getComponent<UnitComponent>(target, ComponentTypes.Unit);
    const attackerPos = world.getComponent<PositionComponent>(attacker, ComponentTypes.Position);
    const targetPos = world.getComponent<PositionComponent>(target, ComponentTypes.Position);

    // 获取完整的地形修正（包含特殊效果）
    let attackerTerrainMod = null;
    let targetTerrainMod = null;

    if (attackerPos && attackerUnit) {
      attackerTerrainMod = getFullTerrainModifier(
        attackerPos.x, attackerPos.z,
        attackerUnit.unitType as UnitType,
        attackerFaction.factionId
      );
    }

    if (targetPos && targetUnit) {
      targetTerrainMod = getFullTerrainModifier(
        targetPos.x, targetPos.z,
        targetUnit.unitType as UnitType,
        targetFaction.factionId
      );
    }

    // 检查闪避
    if (targetTerrainMod && targetTerrainMod.dodgeChance) {
      if (Math.random() < targetTerrainMod.dodgeChance) {
        // 闪避成功，不造成伤害
        EventBus.emit('combat:dodge', {
          targetId: target,
          attackerId: attacker,
        });
        return;
      }
    }

    // Calculate damage
    let damage = combat.attack * damageMultiplier;

    // Apply counter bonus
    if (attackerUnit && targetUnit) {
      damage *= getCounterBonus(attackerUnit.unitType, targetUnit.unitType);
    }

    // Apply faction attack bonus
    const faction = FACTIONS[attackerFaction.factionId];
    if (faction) {
      damage *= faction.attackBonus;
    }

    // Apply terrain attack modifier
    if (attackerTerrainMod) {
      damage *= attackerTerrainMod.attack;
    }

    // 检查暴击
    let isCrit = false;
    if (attackerTerrainMod && attackerTerrainMod.critChance) {
      const critRoll = Math.random();
      if (critRoll < attackerTerrainMod.critChance) {
        damage *= 1.5; // 暴击伤害 +50%
        isCrit = true;
      }
    }

    // Apply defense reduction
    const targetCombat = world.getComponent<CombatComponent>(target, ComponentTypes.Combat);
    if (targetCombat) {
      damage *= 100 / (100 + targetCombat.defense);
    }

    // Apply terrain defense modifier
    if (targetTerrainMod) {
      damage /= targetTerrainMod.defense;
    }

    // Shield special ability: -60% damage from ranged attacks
    if (targetUnit?.unitType === 'shield' && this.isRangedAttacker(attackerUnit?.unitType, attackerBuilding?.buildingType)) {
      damage *= 0.4;
    }

    // Random variance (±10%)
    damage *= 0.9 + Math.random() * 0.2;

    const finalDamage = Math.floor(damage);

    // Apply damage
    targetHealth.current -= finalDamage;

    // 记录攻击者到目标，用于反击机制
    if (targetCombat) {
      targetCombat.lastAttacker = attacker;
      targetCombat.lastAttackerTime = this.gameTime;
    }

    EventBus.emit('unit:attack', {
      attackerId: attacker,
      targetId: target,
      damage: finalDamage,
      isCrit,
    });

    // 检查眩晕效果（地形导致的）
    if (attackerTerrainMod && attackerTerrainMod.stunChance) {
      if (Math.random() < attackerTerrainMod.stunChance) {
        // 触发眩晕效果（这里可以添加眩晕状态组件）
        EventBus.emit('combat:stun', {
          targetId: target,
          duration: 1.5,
        });
      }
    }
  }
}
